import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppCard,
  AppNoInfo,
  AppNotificationAlert,
} from '@/layout/AppComponents';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import TotalEnergiesRiskSidebar from '../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/Sidebar/TotalEnergiesRiskSidebar';
import AppLink from '../../../routing/helpers/AppLink';
import ActionPlanFields from '../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/Switch/Risk/ActionPlanTab/ActionPlanFields';
import * as moduleAction from "../../../store/actions/moduleAction";

export default function ModuleTotalEnergiesRiskActionPlanHomeScreen() {
  const { moduleBridgeHex, riskID, actionPlanHex, page, filtersHex } =
    useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData
  );
  const [loading, setLoading] = useState(false);
  const [actionPlan, setActionPlan] = useState();
  const validationSchema = Yup.object().shape({
    riskID: Yup.string().required('Requerido').notOneOf(['0'], 'Requerido'),
    description: Yup.string().required('Requerido'),
    responsibleID: Yup.number().nullable().required('Requerido'),
    startDateStamp: Yup.string().required('Requerido'),
    endDateStamp: Yup.string().required('Requerido'),
  });

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD ACTION PLAN
  const loadActionPlan = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_totalEnergiesRisk_actionPlan_get'), {
          moduleBridgeHex,
          actionPlanHex,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            setActionPlan(data);
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex, actionPlanHex]
  );

  useEffect(() => {
    if (actionPlanHex) {
      setLoading(true);
      loadActionPlan();
    }
  }, []);
  // < LOAD ACTION PLAN

  // > SAVE
  const handleSave = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            actionPlanHex
              ? 'api_r_user_module_totalEnergiesRisk_actionPlan_edit'
              : 'api_r_user_module_totalEnergiesRisk_actionPlan_add'
          ),
          {
            moduleBridgeHex,
            actionPlanHex,
            ...values,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            if (actionPlanHex) {
              navigate(
                path({
                  name: 'user_module_totalEnergiesRisk_actionPlan_list',
                  params: { moduleBridgeHex, page, filtersHex },
                })
              );
            } else {
              navigate(
                path({
                  name: 'user_module_totalEnergiesRisk_actionPlan_review_edit',
                  params: { moduleBridgeHex, reviewHex: data?.reviewHex },
                })
              );
            }
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, actionPlanHex]
  );
  // < SAVE

  // > CANCEL TOGGLE
  const handleCancelToggle = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_totalEnergiesRisk_actionPlan_cancel_toggle'
          ),
          {
            moduleBridgeHex,
            actionPlanHex,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            AppNotificationAlert({
              title: 'Completado',
              message: data?.isCancel
                ? 'El Plan de accíon ha sido anulado'
                : 'El Plan de accíon ha sido reactivado',
              type: 'success',
              duration: 5000,
            });
            setActionPlan({ ...actionPlan, state: data?.state });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, actionPlan, actionPlanHex]
  );
  // < CANCEL TOGGLE

    // > LOAD MODULE DATA //
    const loadModuleData = useCallback(async () => {
      dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_totalEnergiesRisk_getModuleData"),
          "moduleData",
          {
            moduleBridgeHex,
          }
        )
      );
    }, []);
  
    useEffect(() => {
      loadModuleData();
    }, []);
    // > LOAD MODULE DATA //
  
    // < STORE CALLS
    const loadModulePermissions = useCallback(async () => {
      try {
        await dispatch(
          moduleAction.fetch(
            axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
            "permissions",
            {
              moduleBridgeHex,
              subType: "permissions",
            }
          )
        );
        await dispatch(
          moduleAction.fetch(
            axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
            "userBridge",
            {
              moduleBridgeHex,
              subType: "userBridge",
            }
          )
        );
      } catch (err) {}
    }, [moduleBridgeHex]);
  
    useEffect(() => {
      loadModulePermissions();
    }, [moduleBridgeHex]);

  if (!moduleData) return <AppNoInfo />

  return (
    <>
      <TotalEnergiesRiskSidebar />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: moduleData?.moduleName,
                  route: {
                    name: 'user_module_totalEnergiesRisk_actionPlan_list',
                    params: {
                      moduleBridgeHex,
                      page,
                      filtersHex,
                    },
                  },
                },
                {
                  name: 'Planes de acción',
                  route: {
                    name: 'user_module_totalEnergiesRisk_actionPlan_list',
                    params: {
                      moduleBridgeHex,
                      page,
                      filtersHex,
                    },
                  },
                },
                {
                  name: actionPlanHex
                    ? 'Edición de Plan de acción'
                    : 'Nuevo Plan de acción',
                },
              ]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          {loading ? (
            <AppNoInfo minHeight={window.innerHeight - 60} />
          ) : (
            <Formik
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              initialValues={{
                description: actionPlan?.description ?? '',
                riskID: actionPlan?.riskID ?? riskID ?? null,
                responsibleName: actionPlan?.responsibleName,
                responsibleID: actionPlan?.responsibleID,
                observations: actionPlan?.observations ?? '',
                dateStamp: actionPlan?.dateStamp ?? '',
                startDateStamp: actionPlan?.startDateStamp ?? '',
                endDateStamp: actionPlan?.endDateStamp ?? '',
                modificationDateStamp: actionPlan?.modificationDateStamp ?? '',
              }}
              onSubmit={handleSave}
            >
              {({
                setFieldValue,
                handleSubmit,
                values,
                errors,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <AppCard
                    header={
                      <div>
                        {actionPlan
                          ? 'Edición de Plan de acción'
                          : 'Nuevo Plan de acción'}
                        {actionPlan && (
                          <button
                            type="button"
                            className={`btn btn-xs btn-${
                              actionPlan?.state === 2 ? 'success' : 'danger'
                            } float-right`}
                            onClick={handleCancelToggle}
                          >
                            {actionPlan?.state === 2 ? 'Reactivar' : 'Anular'}
                          </button>
                        )}
                      </div>
                    }
                  >
                    <ActionPlanFields
                      values={values}
                      errors={errors}
                      setFieldValue={setFieldValue}
                    />
                  </AppCard>
                  <div className="row mt-3">
                    <div className="col-12">
                      <button
                        type="submit"
                        className="btn btn-sm btn-success float-right"
                        disabled={
                          isSubmitting ||
                          (actionPlan && actionPlan?.state === 2)
                        }
                      >
                        {actionPlanHex ? 'Editar' : 'Crear'}
                      </button>
                      <AppLink
                        path="user_module_totalEnergiesRisk_actionPlan_list"
                        params={{
                          moduleBridgeHex,
                          page,
                          filtersHex,
                        }}
                        title="Cancelar"
                        className="btn btn-sm btn-outline-danger float-right mr-2"
                      >
                        Volver
                      </AppLink>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          )}
        </div>
      </main>
    </>
  );
}
